import React, {useMemo} from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {Table, TableBody, TableCell, TableRow, Tooltip, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {DeliveryInstructionDTO} from "@/api/data-contracts";
import {CompanyI18N} from "@/translations";

/**
 * Props for the DeliveryInstructionTooltip component.
 *
 * @property {DeliveryInstructionDTO | null | undefined} deliveryInstruction - The delivery instruction data to display.
 */
interface DeliveryInstructionTooltipProps {
    deliveryInstruction?: DeliveryInstructionDTO | null;
}

type DayId = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";

interface DayRow {
    id: DayId;
    label: string;
    value: string | undefined;
}

/**
 * Formats a string value for display in the tooltip.
 * Returns the empty placeholder "—" for blank, null, or undefined values.
 */
const formatValue = (value: string | undefined | null, emptyPlaceholder: string): string =>
        value && value.trim().length > 0 ? value : emptyPlaceholder;

/**
 * Returns true if all relevant fields on the delivery instruction are empty/falsy.
 */
const isDeliveryInstructionEmpty = (di: DeliveryInstructionDTO): boolean => {
    const textFields = [
        di.mondayInstruction,
        di.tuesdayInstruction,
        di.wednesdayInstruction,
        di.thursdayInstruction,
        di.fridayInstruction,
        di.saturdayInstruction,
        di.sundayInstruction,
        di.arrivalNotification
    ];
    const hasText = textFields.some((f) => f && f.trim().length > 0);
    const hasTimeSlot = di.timeSlotBookingRequired === true;
    return !hasText && !hasTimeSlot;
};

/**
 * DeliveryInstructionTooltip displays an info icon with a tooltip listing all delivery
 * instruction details (7 weekdays, time-slot booking flag, and pre-advice note).
 * Empty fields are shown as "—". If the delivery instruction is absent or all fields are empty,
 * the tooltip shows a "no instructions" message. The icon is always visible.
 */
const DeliveryInstructionTooltip: React.FC<DeliveryInstructionTooltipProps> = ({deliveryInstruction}) => {
    const {t} = useTranslation();

    const emptyPlaceholder = t(CompanyI18N.form.tooltip.deliveryInstructionsEmptyValue);

    const tooltipContent = useMemo(() => {
        if (!deliveryInstruction || isDeliveryInstructionEmpty(deliveryInstruction)) {
            return (
                    <Typography variant="body2" sx={{padding: "4px 8px", fontSize: "0.75rem"}}>
                        {t(CompanyI18N.form.tooltip.deliveryInstructionsEmpty)}
                    </Typography>
            );
        }

        const dayRows: DayRow[] = [
            {id: "monday", label: t(CompanyI18N.form.field.mondayShort), value: deliveryInstruction.mondayInstruction},
            {
                id: "tuesday",
                label: t(CompanyI18N.form.field.tuesdayShort),
                value: deliveryInstruction.tuesdayInstruction
            },
            {
                id: "wednesday",
                label: t(CompanyI18N.form.field.wednesdayShort),
                value: deliveryInstruction.wednesdayInstruction
            },
            {
                id: "thursday",
                label: t(CompanyI18N.form.field.thursdayShort),
                value: deliveryInstruction.thursdayInstruction
            },
            {id: "friday", label: t(CompanyI18N.form.field.fridayShort), value: deliveryInstruction.fridayInstruction},
            {
                id: "saturday",
                label: t(CompanyI18N.form.field.saturdayShort),
                value: deliveryInstruction.saturdayInstruction
            },
            {id: "sunday", label: t(CompanyI18N.form.field.sundayShort), value: deliveryInstruction.sundayInstruction}
        ];

        const timeSlotValue = deliveryInstruction.timeSlotBookingRequired
                ? t(CompanyI18N.form.field.timeSlotBookingYes)
                : t(CompanyI18N.form.field.timeSlotBookingNo);

        return (
                <Table
                        size="small"
                        sx={{
                            "& td, & th": {
                                padding: "4px 8px",
                                fontSize: "0.75rem",
                                borderColor: "divider"
                            }
                        }}
                >
                    <TableBody>
                        {dayRows.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell component="th" sx={{fontWeight: "bold"}}>
                                        {row.label}
                                    </TableCell>
                                    <TableCell>{formatValue(row.value, emptyPlaceholder)}</TableCell>
                                </TableRow>
                        ))}
                        <TableRow>
                            <TableCell component="th" sx={{fontWeight: "bold"}}>
                                {t(CompanyI18N.form.field.isTimeSlotBooking)}
                            </TableCell>
                            <TableCell>{timeSlotValue}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell component="th" sx={{fontWeight: "bold"}}>
                                {t(CompanyI18N.form.field.preAdviceNote)}
                            </TableCell>
                            <TableCell>{formatValue(deliveryInstruction.arrivalNotification, emptyPlaceholder)}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
        );
    }, [deliveryInstruction, t, emptyPlaceholder]);

    return (
            <Tooltip
                    arrow
                    title={tooltipContent}
                    slotProps={{
                        tooltip: {
                            sx: {
                                maxWidth: "fit-content",
                                padding: "4px",
                                backgroundColor: "background.paper",
                                color: "text.primary"
                            }
                        },
                        arrow: {
                            sx: {
                                color: "background.paper"
                            }
                        }
                    }}
            >
                <InfoOutlinedIcon fontSize="small" sx={{cursor: "pointer", color: "action.active"}}/>
            </Tooltip>
    );
};

export default DeliveryInstructionTooltip;
